import React, { useState } from 'react';
import { View, StyleSheet, Text, Platform, ScrollView, Alert } from 'react-native';
import NavBar from 'src/presentation/components/organisms/NavBar';
import Loader from 'src/presentation/components/atoms/Loader';
import Footer from 'src/presentation/components/organisms/Footer';
import { useFooterHeight } from 'src/presentation/hooks/useFooterHeight';
import TopicusListItem from 'src/presentation/components/molecules/TopicusListItem';
import { MaterialIcons } from '@expo/vector-icons';
import ImagePicker from 'src/presentation/components/molecules/ImagePicker';
import * as ImagePick from 'expo-image-picker';
import * as FileSystem from 'expo-file-system/legacy';
import { Camera } from 'expo-camera';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useGalleryStore } from 'src/domain/stores/useGalleryStore';
import ChooseGalleryModal from 'src/presentation/modals/ChooseGalleryModal';
import { useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { AlertButton } from 'react-native';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Dimens from 'src/core/utils/Dimens';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import Translations from 'src/core/utils/Translations';
import errorHandler from 'src/core/utils/errorHandler';
import logError from 'src/data/services/logError';
import Fonts from 'src/core/utils/Fonts';
import Colors from 'src/core/utils/Colors';
import type Constants from 'src/core/utils/Constants';
import { usePageFromRoute } from 'src/presentation/hooks/usePageFromRoute';
import type { ApiPageGallery } from 'src/core/models/api/ApiPage';

type UploadedImage = { base64?: string | null; uri: string };

function GalleryHolderScreen() {
	const route = useRoute<RouteProp<RootStackParamList, typeof Constants.screenGalleryHolder>>();
	const eventStore = useEventStore();
	const galleryStore = useGalleryStore();
	const { page: data } = usePageFromRoute(route?.params, { type: 'GalleryHolder' });
	const holderPage = data?.type === 'GalleryHolder' ? data : null;
	const title = holderPage?.name ?? '';
	const galleries: ApiPageGallery[] = holderPage?.children ?? [];
	const footerHeight = useFooterHeight();

	const [isLoading, setIsLoading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState<number | null>(null);
	const [showModal, setShowModal] = useState(false);

	const toggleModal = () => {
		setShowModal((prev) => !prev);
	};

	const askPermissionsAsync = async () => {
		if (Platform.OS !== 'web') {
			const cameraPermission = await Camera.requestCameraPermissionsAsync();
			let mediaLibraryPermission = await ImagePick.requestMediaLibraryPermissionsAsync();

			if (mediaLibraryPermission.status !== 'granted') {
				mediaLibraryPermission = await ImagePick.requestMediaLibraryPermissionsAsync();
			}

			if (mediaLibraryPermission.status !== 'granted') {
				errorHandler(Translations.errorPhotoPermissionTitle, Translations.errorPhotoPermissionMessage);
			} else if (cameraPermission.status !== 'granted') {
				errorHandler(Translations.errorCameraNotAvailableTitle, Translations.errorCameraNotAvailableMessage);
			}
		}
	};

	const resolveBase64 = (result: UploadedImage): Promise<string | null> => {
		if (result.base64) {
			return Promise.resolve(result.base64);
		}
		// On Android, launchCameraAsync may not populate base64 even when requested. Not a web path:
		// expo-file-system is a shim there without readAsStringAsync, so this would throw where the
		// picker already fills base64 for us.
		if (result.uri && Platform.OS !== 'web') {
			return FileSystem.readAsStringAsync(result.uri, { encoding: FileSystem.EncodingType.Base64 });
		}
		return Promise.resolve(null);
	};

	const uploadPhoto = async (result: UploadedImage, chosenGallery: ApiPageGallery) => {
		const eventUuid = eventStore.activeEventUuid;
		const galleryID = chosenGallery.id;

		setIsLoading(true);
		setUploadProgress(null);

		let base64: string | null = null;
		try {
			base64 = await resolveBase64(result);
		} catch {
			setIsLoading(false);
			setUploadProgress(null);
			errorHandler(Translations.errorSomethingWentWrong, Translations.errorTryAgainLater);
			return;
		}

		if (!base64) {
			setIsLoading(false);
			setUploadProgress(null);
			errorHandler(Translations.errorSomethingWentWrong, Translations.errorTryAgainLater);
			return;
		}

		try {
			const response = await galleryStore.uploadPhoto({
				eventUuid,
				galleryId: galleryID,
				onUploadProgress: (event) => {
					const total = event.total;
					if (total && total > 0) {
						setUploadProgress(Math.round((event.loaded / total) * 100));
					}
				},
				payload: { base64 },
			});

			if (response.status === 'success') {
				errorHandler(Translations.successUploadedTitle, Translations.successUploadedMessage);
			} else {
				errorHandler(Translations.errorSomethingWentWrong, Translations.errorTryAgainLater);
			}
		} catch {
			errorHandler(Translations.errorSomethingWentWrong, Translations.errorTryAgainLater);
		} finally {
			setIsLoading(false);
			setUploadProgress(null);
		}
	};

	const pickImageFromCamera = async (gallery: ApiPageGallery) => {
		const options = {
			base64: true,
			quality: 0.4,
			allowsEditing: true,
		};
		await askPermissionsAsync();

		const response = await ImagePick.launchCameraAsync(options);
		if (!response.canceled && response.assets?.[0]) {
			await uploadPhoto(response.assets[0], gallery);
		}
	};

	const pickImageFromCameraRoll = async (gallery: ApiPageGallery) => {
		const options = {
			base64: true,
			quality: 0.4,
		};
		await askPermissionsAsync();
		const response = await ImagePick.launchImageLibraryAsync(options);
		if (!response.canceled && response.assets?.[0]) {
			await uploadPhoto(response.assets[0], gallery);
		}
	};

	const pickImage = (gallery: ApiPageGallery) => {
		toggleModal();

		// A browser has no camera-or-library question to ask: the file picker it opens
		// offers both, and on a phone it offers the camera itself. React Native's Alert
		// is an empty function on web, so asking here closed the chooser and then did
		// nothing at all -- which is how "adding a photo does nothing on web" came in.
		if (Platform.OS === 'web') {
			pickImageFromCameraRoll(gallery).catch(logError);

			return;
		}

		const buttons: AlertButton[] = [
			{
				text: Translations.alertOpenCamera,
				onPress: () => pickImageFromCamera(gallery),
				isPreferred: true,
			},
			{
				text: Translations.alertOpenCameraRoll,
				onPress: () => pickImageFromCameraRoll(gallery),
			},
			{
				text: Translations.buttonCancel,
				style: 'cancel',
			},
		];

		if (Platform.OS === 'android') {
			buttons.reverse();
		}

		// A browser never reaches this: the web branch above opens the file picker directly, because a
		// browser has no camera roll to choose from.
		// eslint-disable-next-line no-restricted-syntax -- native-only, guarded above.
		Alert.alert(
			Translations.alertAddPhotoTitle,
			Translations.alertAddPhotoMessage.replace('{{name}}', gallery.name),
			buttons,
		);
	};

	const renderGalleryList = (galleryList: ApiPageGallery[]) => {
		const filtered = galleryList.filter((gallery) => String(gallery.hide_on_home) !== '1');
		const lengthArray = filtered.length - 1;
		return filtered.map((gallery, index) => (
			<TopicusListItem
				data={gallery}
				image={gallery.data?.page_image}
				key={index}
				noBorderBottom={lengthArray === index}
				title={gallery.name}
			/>
		));
	};

	return (
		<View style={{ flex: 1 }}>
			<NavBar
				backBtn={true}
				bgImage={eventStore.activeTheme.header_bg_image}
				bgcolor={eventStore.activeTheme.header_bg_color}
				color={eventStore.activeTheme.header_text_color}
				editBtn={false}
				statusBarStyle={eventStore.activeTheme.statusbar_color}
				title={title}
			/>
			{isLoading ? (
				<Loader message={uploadProgress !== null ? `Uploaden… ${uploadProgress}%` : 'Uploaden…'} />
			) : (
				<ScrollView contentContainerStyle={{ paddingBottom: footerHeight }} style={StyleSheet.flatten(styles.container)}>
					<View style={styles.list}>
						<ChooseGalleryModal
							closeModal={toggleModal}
							galleries={galleries}
							modalVisible={showModal}
							onGalleryClick={(gallery) => pickImage(gallery)}
						/>
						<ImagePicker pickImage={() => toggleModal()}>
							<View style={styles.noLine}>
								<MaterialIcons name="backup" size={43} style={{ color: Colors.greyDark }} />
								<View style={{ justifyContent: 'center', flex: 1 }}>
									<Text style={styles.text}>{Translations.textUploadPhoto}</Text>
								</View>
							</View>
						</ImagePicker>
					</View>
					<Text style={[styles.heading, { color: eventStore.activeTheme.heading_text_color }]}>
						{Translations.textGalleries}
					</Text>
					<View style={styles.list}>
						{(() => {
							const filtered = galleries.filter((gallery) => String(gallery.hide_on_home) !== '1');
							if (filtered.length === 0) {
								return (
									<EmptyState
										icon="images-outline"
										message={Translations.textNoGalleriesMessage}
										title={Translations.textNoGalleriesTitle}
									/>
								);
							}
							return renderGalleryList(galleries);
						})()}
					</View>
				</ScrollView>
			)}
			<Footer
				backgroundColor={eventStore.activeTheme.footer_bg_color}
				backgroundImage={eventStore.activeTheme.footer_bg_image}
				backgroundType={eventStore.activeTheme.footer_bg_type}
			/>
		</View>
	);
}

export default GalleryHolderScreen;

const styles = StyleSheet.create({
	container: {
		backgroundColor: Platform.OS === 'android' ? Colors.white : undefined,
	},
	heading: {
		color: Platform.OS === 'android' ? Colors.blackPure : Colors.greyHeader,
		fontSize: Platform.OS === 'android' ? Dimens.fontSize14 : Dimens.fontSize20,
		paddingLeft: Platform.OS === 'android' ? Dimens.padding16 : Dimens.padding20,
		paddingRight: Platform.OS === 'android' ? Dimens.padding16 : Dimens.padding20,
		marginTop: Platform.OS === 'android' ? Dimens.margin20 : Dimens.margin20,
		marginBottom: Platform.OS === 'android' ? Dimens.margin10 : Dimens.margin10,
		fontFamily: Platform.OS === 'android' ? Fonts.montserratMedium : Fonts.montserratMedium,
		opacity: Platform.OS === 'android' ? 0.54 : undefined,
	},
	list: {
		backgroundColor: Colors.white,
		borderBottomWidth: Platform.OS === 'android' ? 0 : 0.5,
		borderTopWidth: Platform.OS === 'android' ? 0 : 0.5,
		borderColor: Colors.overlay15,
	},
	noLine: {
		flex: 1,
		flexDirection: 'row',
		flexWrap: 'nowrap',
		alignItems: 'center',
		paddingTop: Platform.OS === 'ios' ? Dimens.padding15 : Dimens.padding10,
		paddingBottom: Platform.OS === 'ios' ? Dimens.padding15 : Dimens.padding10,
		marginLeft: Platform.OS === 'ios' ? Dimens.margin20 : Dimens.margin16,
		borderBottomWidth: Platform.OS === 'ios' ? 0 : undefined,
		borderColor: Platform.OS === 'ios' ? Colors.overlay15 : undefined,
	},
	text: {
		fontSize: Dimens.fontSize17,
		color: Colors.greyTextDark,
		fontFamily: Fonts.montserratRegular,
		paddingLeft: Dimens.padding20,
		paddingRight: Dimens.padding20,
		lineHeight: Platform.OS === 'ios' ? Dimens.lineHeight23 : 17,
	},
});
